import React, { useMemo, useEffect, useReducer, useState, useContext } from 'react';

import {
  IDiscoveryBoxSettings,
  IFundRaisingSettings,
  IGiftPackagingSettings,
  IPayPalSettings,
  ICartPrice,
  IAdyenSettings,
} from '@model';
import { useErrorContext } from '@services/errorService';
import { ReactFCC } from '@types';
import { nonAwaitedPromiseHandler } from '@utils/async';
import { SessionStorageKeys } from '@utils/constants';
import { noop, noopPromise } from '@utils/general';

import { IProduct, IGiftActions, IServices, ILastOrder, IDiscount, IBenefits, IGift, IDiscoveryBox } from '../model';
import { useCartPageConfigurationOnRender, useValidateLastOrderOnDemand, useGetCartContentOnRender } from '../services';

import {
  cartPageReducer,
  useActions,
  IUpdateItemInCartAction,
  IRemoveAllItemEntriesFromCartAction,
  IToggleInsuranceAction,
  IToggleEcoPackagingAction,
  IRemoveNonStandardProductAction,
  IRemoveVoucherAction,
  IAddGiftAction,
  IRemoveGiftAction,
  IGiftSingleSelectUpdateAction,
  reducerInitialState,
  IRemoveGiftPackagingAction,
  CartPageReducer,
  IToggleFundRaisingAction,
  IUpdateGiftPackagingAction,
  IAddProductAction,
  ICartPageUpdateCompleteStateAction,
  IRemoveFreeDeliveryProductAction,
  ISetProductVoucherCodeAction,
} from './cartPageContextReducer';
import { ECartPageState, CART_PAGE_DEFAULT_SETTINGS } from './model';

interface ICartPageContextState {
  benefits: IBenefits;
  products: IProduct[];
  discounts: IDiscount[];
  gifts: IGiftActions[];
  serviceItems: IServices;
  pricesSummary: ICartPrice;
  cartPageState: ECartPageState;
  lastOrder: ILastOrder | null;
  proceedCheckoutEnabled: boolean;
  copiedProductVoucherCode: string;
  loyaltyRewardRemoved: boolean;
}

interface ICartPageContextActions {
  updateItem: (data: IUpdateItemInCartAction) => Promise<boolean>;
  removeAllItemEntries: (data: IRemoveAllItemEntriesFromCartAction) => void;
  removeGiftPackaging: (data: IRemoveGiftPackagingAction) => Promise<boolean>;
  addGiftPackaging: (data: IUpdateGiftPackagingAction) => void;
  toggleInsurance: (data: IToggleInsuranceAction) => void;
  toggleEcoPackaging: (data: IToggleEcoPackagingAction) => void;
  removeNonStandardProduct: (data: IRemoveNonStandardProductAction) => void;
  removeVoucher: (data: IRemoveVoucherAction) => void;
  addVoucher: (voucherCode: string) => Promise<string[]>;
  addGift: (data: IAddGiftAction, giftsList: IGift[]) => Promise<boolean>;
  removeGift: (data: IRemoveGiftAction, giftsList: IGift[]) => void;
  giftSingleSelectUpdate: (data: IGiftSingleSelectUpdateAction, giftsList: IGift[]) => Promise<boolean>;
  setCartPageState: (data: ECartPageState) => void;
  setLastOrder: (data: ILastOrder | null) => void;
  toggleFundRaising: (data: IToggleFundRaisingAction) => void;
  removeDiscoveryBox: (data: number, discoveryBox: IDiscoveryBox) => void;
  validateCart: () => Promise<void>;
  addProductToCart: (data: IAddProductAction) => Promise<number>;
  updateCompleteState: (data: ICartPageUpdateCompleteStateAction) => void;
  removeFreeDeliveryProduct: (data: IRemoveFreeDeliveryProductAction) => void;
  setProductVoucherCode: (data: ISetProductVoucherCodeAction) => void;
}

interface ICartPageContextSettings {
  giftPackaging: IGiftPackagingSettings;
  ecoPackagingEnabled: boolean;
  orderAsLastTimeEnabled: boolean;
  ecoPackagingPrice: number;
  giftPackagingPrice: number;
  giftPackagingLinkUrl: string;
  loyaltyModalImageUrl: string;
  insuranceEnabled: boolean;
  insurancePrice: number;
  freeDelivery: boolean;
  freeDeliveryGlobalLimit: number;
  freeDeliveryGlobalFrom: Date;
  freeDeliveryGlobalTo: Date;
  paypal: IPayPalSettings;
  adyen: IAdyenSettings;
  minimalOrderPriceThreshold: number;
  orderPriceActivationThreshold: number;
  isDeliveryBenefitEnabled: boolean;
  fundRaising: IFundRaisingSettings;
  discoveryBox: IDiscoveryBoxSettings;
  showGiftSelectionByDefault: boolean;
  minimalPriceLimit: number;
  insuranceLinkUrl: string;
  voucherUnderProductEnabled: boolean;
}

export interface ICartPageContext {
  state: ICartPageContextState;
  actions: ICartPageContextActions;
  settings: ICartPageContextSettings;
}

const defaultContextValue: ICartPageContext = {
  state: {
    benefits: { loyalties: [], vouchers: [] },
    products: [],
    discounts: [],
    gifts: [],
    serviceItems: {
      giftPackagings: [],
      insurance: null,
      ecoPackaging: null,
      fundRaising: null,
      discoveryBox: [],
    },
    pricesSummary: {
      priceDelivery: 0,
      priceItemSummary: 0,
      pricePayment: 0,
      priceProductsSummary: 0,
      discountWithoutDelivery: 0,
      vat: 0,
    },
    cartPageState: ECartPageState.LOADING,
    lastOrder: null,
    proceedCheckoutEnabled: false,
    copiedProductVoucherCode: '',
    loyaltyRewardRemoved: false,
  },
  actions: {
    addGift: noopPromise,
    addGiftPackaging: noop,
    addVoucher: noopPromise,
    giftSingleSelectUpdate: noopPromise,
    removeAllItemEntries: noop,
    removeNonStandardProduct: noop,
    removeGift: noop,
    removeGiftPackaging: noopPromise,
    removeVoucher: noop,
    toggleEcoPackaging: noop,
    toggleInsurance: noop,
    updateItem: noopPromise,
    setCartPageState: noop,
    setLastOrder: noop,
    toggleFundRaising: noop,
    removeDiscoveryBox: noop,
    validateCart: noopPromise,
    addProductToCart: noopPromise,
    updateCompleteState: noop,
    removeFreeDeliveryProduct: noop,
    setProductVoucherCode: noop,
  },
  settings: CART_PAGE_DEFAULT_SETTINGS,
};

export const CartPageContext = React.createContext<ICartPageContext>(defaultContextValue);

export const CartPageContextProvider: ReactFCC = ({ children }) => {
  const { loading: cartContentLoading, response: cartContentResponse } = useGetCartContentOnRender();
  const { response: cartPageSettingsResponse } = useCartPageConfigurationOnRender();
  const { request: validateLastOrderRequest, response: validateLastOrderResponse } = useValidateLastOrderOnDemand();

  const { setFatalError } = useErrorContext();

  const [state, dispatch] = useReducer<CartPageReducer>(cartPageReducer, reducerInitialState);
  const actions = useActions(dispatch);

  const { updateCompleteState } = actions;

  const [cartPageState, setCartPageState] = useState<ECartPageState>(ECartPageState.LOADING);
  const [lastOrder, setLastOrder] = useState<ILastOrder | null>(null);
  const [cartPageSettings, setCartPageSettings] = useState<ICartPageContextSettings>(CART_PAGE_DEFAULT_SETTINGS);

  useEffect(() => {
    if (cartContentLoading) {
      return;
    }

    if (!cartContentLoading && cartContentResponse?.data) {
      updateCompleteState(cartContentResponse.data);
      setCartPageState(ECartPageState.DEFAULT);
      return;
    }

    setFatalError(true);
  }, [cartContentResponse, cartContentLoading, updateCompleteState, setFatalError]);

  useEffect(() => {
    if (cartPageSettingsResponse) {
      setCartPageSettings({ ...cartPageSettingsResponse.data });

      if (cartPageSettingsResponse.data.orderAsLastTimeEnabled) {
        nonAwaitedPromiseHandler(validateLastOrderRequest);
      }
    }
  }, [setCartPageSettings, cartPageSettingsResponse, validateLastOrderRequest]);

  useEffect(() => {
    setLastOrder(validateLastOrderResponse?.isValid ? validateLastOrderResponse?.lastOrder : null);

    // check DaPPage date ae not set up
    const hasDapPageData = Boolean(sessionStorage.getItem(SessionStorageKeys.DAP_PAGE_DATA));

    if (!hasDapPageData && validateLastOrderResponse?.lastOrder) {
      // set Dap Data
      const dapPageData = {
        paymentId: validateLastOrderResponse?.lastOrder?.paymentInfo.paymentType,
        carrierId: 0,
        deliveryTypeId: validateLastOrderResponse?.lastOrder?.deliveryInfo.deliveryTypeId,
        pickUpPointCode: validateLastOrderResponse?.lastOrder?.deliveryInfo.distributionPoint.distributionPointId,
        pickUpValidationData: null,
        savePaymentToken: false,
        airDeliveryZipCode: '',
      };

      sessionStorage.setItem(SessionStorageKeys.DAP_PAGE_DATA, JSON.stringify(dapPageData));
    }
  }, [setLastOrder, validateLastOrderResponse]);

  const value = useMemo(
    () => ({
      state: { ...state, cartPageState, lastOrder },
      actions: { ...actions, setCartPageState, setLastOrder },
      settings: cartPageSettings,
    }),
    [state, cartPageState, setCartPageState, actions, lastOrder, cartPageSettings],
  );
  return <CartPageContext.Provider value={value}>{children}</CartPageContext.Provider>;
};

export const useCartPageContext = () => useContext(CartPageContext);
